export type RocqRuntimeStatus = { phase: 'starting' | 'ready' | 'error'; message?: string };

interface RuntimeManager {
  navEnabled: boolean;
  when_ready: { promise: Promise<unknown> };
  coqReady(...args: any[]): unknown;
  goNext(...args: any[]): boolean;
  goPrev(...args: any[]): boolean;
  goCursor(...args: any[]): unknown;
  work(): void;
  reset(): Promise<unknown>;
  coqBoot?(): Promise<void>;
  coqCoqExn?(error: { msg?: string }): unknown;
  coq?: { interruptSetup(): void } | null;
  layout?: { buttons?: HTMLElement };
  doc?: { goals: unknown[]; goals_raw?: Record<number, unknown> };
}

/** A visible CodeMirror is not evidence that the worker's STM is initialized. */
export function guardJsCoqReadiness(
  manager: RuntimeManager,
  onStatus: (status: RocqRuntimeStatus) => void,
  timeoutMs = 120_000,
) {
  let ready = false;
  let disposed = false;
  let resetting = false;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const originals = {
    coqReady: manager.coqReady, goNext: manager.goNext, goPrev: manager.goPrev,
    goCursor: manager.goCursor, work: manager.work, reset: manager.reset,
    coqBoot: manager.coqBoot, coqCoqExn: manager.coqCoqExn,
  };
  const buttons = (enabled: boolean) => {
    manager.layout?.buttons?.querySelectorAll<HTMLButtonElement>('button[name="up"], button[name="down"], button[name="to-cursor"]').forEach(button => { button.disabled = !enabled; });
  };
  const fail = (error?: unknown) => {
    if (disposed) return;
    clearTimeout(timer);
    ready = false;
    resetting = false;
    buttons(false);
    onStatus({ phase: 'error', message: error instanceof Error ? error.message : 'Rocq did not finish starting. Check your connection and try again. Your code is preserved.' });
  };
  const starting = () => {
    ready = false;
    generation++;
    clearTimeout(timer);
    buttons(false);
    onStatus({ phase: 'starting' });
    timer = setTimeout(() => fail(), timeoutMs);
  };
  const markReady = () => {
    if (disposed) return;
    ready = true;
    resetting = false;
    clearTimeout(timer);
    buttons(true);
    onStatus({ phase: 'ready' });
  };

  manager.coqReady = function(...args) {
    const result = originals.coqReady.apply(this, args);
    markReady();
    return result;
  };
  manager.goNext = function(...args) {
    return ready && !disposed && this.navEnabled ? originals.goNext.apply(this, args) : false;
  };
  manager.goPrev = function(...args) {
    return ready && !disposed && this.navEnabled ? originals.goPrev.apply(this, args) : false;
  };
  manager.goCursor = function(...args) {
    if (ready && !disposed && this.navEnabled) return originals.goCursor.apply(this, args);
  };
  manager.work = function() {
    if (ready && !disposed) originals.work.call(this);
  };
  manager.reset = async function() {
    starting();
    resetting = true;
    if (this.doc) {
      this.doc.goals = [];
      this.doc.goals_raw = {};
    }
    try { return await originals.reset.call(this); }
    catch (error) { fail(error); throw error; }
  };
  if (originals.coqBoot) {
    manager.coqBoot = async function() {
      if (resetting) {
        // WA restart emits Boot, but native reset already reloads packages and
        // sends Init. Running the launch-time boot path too races two Inits.
        this.coq?.interruptSetup();
        return;
      }
      await originals.coqBoot!.call(this);
    };
  }
  if (originals.coqCoqExn) {
    manager.coqCoqExn = function(error) {
      const result = originals.coqCoqExn!.call(this, error);
      if (!ready) fail(new Error(error.msg || 'Rocq failed to initialize. Your code is preserved. Try again.'));
      return result;
    };
  }

  starting();
  const initialGeneration = generation;
  // Also support a manager whose Ready event arrived before JsCoq.start returned.
  void manager.when_ready.promise.then(() => {
    if (generation === initialGeneration) markReady();
  }, error => { if (generation === initialGeneration) fail(error); });

  return () => {
    disposed = true;
    clearTimeout(timer);
    Object.assign(manager, originals);
  };
}
